/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { WitboostTemplate } from '@agilelab/plugin-wb-builder-common';
import { Entity, stringifyEntityRef } from '@mainstage/catalog-model';
import { Progress, WarningPanel } from '@mainstage/core-components';
import { TemplateEntityV1beta3 } from '@mainstage/plugin-scaffolder-common';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useScaffolderPageContext } from '../ScaffolderPage/context/useScaffolderPageContext';
import { CustomTemplate } from '../ScaffolderPage/utils';
import { TemplateCard } from './TemplateCard';

// Template cards mount several expensive hooks/components (permissions,
// favorites, markdown, icons, actions). Rendering every card eagerly can block
// the browser main thread on large catalogs, so this list only mounts rows near
// the viewport and represents the rest with spacer rows.
//
// These values are coupled to the CSS grid below and to the current card height.
// If the card layout, gap, minimum width, or max cards per row changes, update
// the virtualizer constants at the same time to avoid blank gaps or jumpy scroll.
const CARD_MIN_WIDTH = 320;
const CARD_GRID_GAP = 16;
const CARD_ROW_HEIGHT = 360;
const MAX_CARDS_PER_ROW = 6;
const OVERSCAN_ROWS = 2;

type VirtualGridRange = {
  columns: number;
  startRow: number;
  endRow: number;
};

// Bounds row indexes so spacer calculations never point outside the list.
const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// Mirrors the CSS grid column calculation for a container width. This keeps the
// virtual row math aligned with the actual number of cards rendered per row.
const getColumnCount = (width: number) =>
  Math.min(
    MAX_CARDS_PER_ROW,
    Math.max(
      1,
      Math.floor((width + CARD_GRID_GAP) / (CARD_MIN_WIDTH + CARD_GRID_GAP)),
    ),
  );

// Tracks which card rows intersect the browser viewport. The page uses window
// scrolling, not an internal scroll container, so the range is calculated from
// the grid position in the document and the current window height.
const useWindowVirtualGrid = (
  itemCount: number,
  containerRef: React.RefObject<HTMLDivElement>,
) => {
  const [range, setRange] = useState<VirtualGridRange>({
    columns: 1,
    startRow: 0,
    endRow: 0,
  });

  useEffect(() => {
    const element = containerRef.current;

    if (!element || typeof window === 'undefined') {
      return undefined;
    }

    // Recompute the mounted row window after scroll, resize, or grid width
    // changes. Only a small overscanned range is mounted; all other rows are
    // represented by spacer elements in the render body below.
    const updateRange = () => {
      // Match the number of columns produced by the CSS grid. The max column cap
      // keeps zoomed-out or very wide screens from mounting too many cards.
      const columns = getColumnCount(element.clientWidth);
      const rowCount = Math.ceil(itemCount / columns);
      const rect = element.getBoundingClientRect();
      const visibleTop = -rect.top;
      const visibleBottom = window.innerHeight - rect.top;
      // Overscan gives the browser a small buffer so cards are mounted before
      // they enter the viewport during normal scrolling.
      const startRow = clamp(
        Math.floor(visibleTop / CARD_ROW_HEIGHT) - OVERSCAN_ROWS,
        0,
        rowCount,
      );
      const endRow = clamp(
        Math.ceil(visibleBottom / CARD_ROW_HEIGHT) + OVERSCAN_ROWS,
        startRow,
        rowCount,
      );

      setRange(previousRange => {
        if (
          previousRange.columns === columns &&
          previousRange.startRow === startRow &&
          previousRange.endRow === endRow
        ) {
          return previousRange;
        }

        return { columns, startRow, endRow };
      });
    };

    updateRange();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateRange)
        : undefined;
    resizeObserver?.observe(element);

    document.addEventListener('scroll', updateRange, true);
    window.addEventListener('resize', updateRange);

    return () => {
      resizeObserver?.disconnect();
      document.removeEventListener('scroll', updateRange, true);
      window.removeEventListener('resize', updateRange);
    };
  }, [containerRef, itemCount]);

  return range;
};

/**
 * @internal
 */
export type TemplateListProps = {
  group?: {
    title?: ReactNode;
    filter: (entity: any) => boolean;
  };
  witboostCustomTemplates: { [p: string]: CustomTemplate[] };
};

const useStyles = makeStyles(
  theme => ({
    root: {
      '&:first-child': {
        marginTop: theme.spacing(1),
      },
      '&:last-child': {
        marginBottom: 0,
      },
      marginBottom: theme.spacing(6),
    },
    virtualCards: {
      paddingTop: theme.spacing(1),
      gap: theme.spacing(2),
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
      maxWidth: `calc(${MAX_CARDS_PER_ROW} * ${CARD_MIN_WIDTH}px + ${
        MAX_CARDS_PER_ROW - 1
      } * ${CARD_GRID_GAP}px)`,
    },
    virtualSpacer: {
      gridColumn: '1 / -1',
    },
  }),
  { name: 'TemplatesGroup' },
);

/**
 * @internal
 */
export const TemplateList = ({
  group,
  witboostCustomTemplates,
}: TemplateListProps) => {
  const { loading, error, entities, errors, TemplateCardComponent } =
    useScaffolderPageContext();

  const classes = useStyles();
  const virtualGridRef = useRef<HTMLDivElement>(null);

  const CustomTemplateCard = TemplateCardComponent || TemplateCard;

  const maybeFilteredEntities = useMemo(
    () => (group ? entities.filter(e => group.filter(e)) : entities),
    [entities, group],
  );

  const errorsByEntityId = useMemo(
    () =>
      new Map(errors?.map(entityError => [entityError.entity_id, entityError])),
    [errors],
  );

  const virtualRange = useWindowVirtualGrid(
    maybeFilteredEntities.length,
    virtualGridRef,
  );

  const rowCount = Math.ceil(
    maybeFilteredEntities.length / virtualRange.columns,
  );
  const startIndex = virtualRange.startRow * virtualRange.columns;
  const endIndex = Math.min(
    virtualRange.endRow * virtualRange.columns,
    maybeFilteredEntities.length,
  );
  const virtualEntities = maybeFilteredEntities.slice(startIndex, endIndex);
  // The spacer rows preserve the document scroll height while offscreen cards
  // stay unmounted. They must use the same row estimate used above.
  const topSpacerHeight = virtualRange.startRow * CARD_ROW_HEIGHT;
  const bottomSpacerHeight =
    Math.max(rowCount - virtualRange.endRow, 0) * CARD_ROW_HEIGHT;

  const titleComponent: ReactNode = (() => {
    let title: ReactNode = 'Other templates';
    if (group && group.title) {
      title = group.title;
    }

    if (typeof title === 'string') {
      return (
        <Typography
          style={{ fontWeight: 'bold' }}
          variant="h5"
          color="secondary"
        >
          {title}
        </Typography>
      );
    }

    return title;
  })();

  if (group && maybeFilteredEntities.length === 0) {
    return null;
  }
  return (
    <>
      {loading && <Progress />}

      {error && (
        <WarningPanel title="Oops! Something went wrong loading the templates">
          {error.message}
        </WarningPanel>
      )}

      <Box className={classes.root}>
        {titleComponent}
        <div ref={virtualGridRef} className={classes.virtualCards}>
          {topSpacerHeight > 0 && (
            <div
              className={classes.virtualSpacer}
              style={{ height: topSpacerHeight }}
            />
          )}
          {virtualEntities.map((template: Entity) => (
            <Box key={stringifyEntityRef(template)}>
              <CustomTemplateCard
                error={errorsByEntityId.get(template.metadata.uid)}
                template={template as TemplateEntityV1beta3}
                customTemplates={
                  witboostCustomTemplates[
                    (
                      template as WitboostTemplate
                    ).spec.steps[0].input.values.useCaseTemplateId?.toLocaleLowerCase()
                  ]
                }
              />
            </Box>
          ))}
          {bottomSpacerHeight > 0 && (
            <div
              className={classes.virtualSpacer}
              style={{ height: bottomSpacerHeight }}
            />
          )}
        </div>
      </Box>
    </>
  );
};
